import { useEffect, useMemo, useState } from "react";
import api from "../../../api/api";
import WebSocketService from "../../../services/WebSocketService";

export default function IoTLiveControlCenterPage() {
    const [devices, setDevices] = useState([]);
    const [liveEvents, setLiveEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const loadDevices = async () => {
        setLoading(true);
        setError("");

        try {
            const res = await api.get("/IoTDevice");
            setDevices(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Failed to load IoT devices", err);
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to load IoT devices."
            );
            setDevices([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDevices();
    }, []);

    useEffect(() => {
        const ws = new WebSocketService();

        ws.onMessage = (raw) => {
            try {
                const msg = JSON.parse(raw);

                if (msg.type === "iot_update" && msg.trackingNumber) {
                    setDevices((prev) =>
                        prev.map((d) =>
                            d.trackingNumber === msg.trackingNumber
                                ? {
                                    ...d,
                                    latitude: msg.latitude ?? d.latitude,
                                    longitude: msg.longitude ?? d.longitude,
                                    speedKmh: msg.speedKmh ?? d.speedKmh,
                                    headingDegrees:
                                        msg.headingDegrees ?? d.headingDegrees,
                                    accuracyMeters:
                                        msg.accuracyMeters ?? d.accuracyMeters,
                                    signalQuality:
                                        msg.signalQuality ?? d.signalQuality,
                                    batteryLevel:
                                        msg.batteryLevel ?? d.batteryLevel,
                                    temperature:
                                        msg.temperature ?? d.temperature,
                                    humidity: msg.humidity ?? d.humidity,
                                    lastUpdated:
                                        msg.updatedAt ||
                                        new Date().toISOString(),
                                }
                                : d
                        )
                    );
                }

                setLiveEvents((prev) =>
                    [
                        {
                            id: `${Date.now()}-${Math.random()}`,
                            type: msg.type || "IoT Event",
                            message:
                                msg.trackingNumber ||
                                msg.message ||
                                "Live IoT update",
                            time: new Date().toLocaleTimeString(),
                        },
                        ...prev,
                    ].slice(0, 10)
                );
            } catch (err) {
                console.error("IoT Live WS error", err);
            }
        };

        ws.connect();

        return () => {
            if (typeof ws.close === "function") ws.close();
            else if (typeof ws.disconnect === "function") ws.disconnect();
        };
    }, []);

    const stats = useMemo(() => {
        const total = devices.length;
        const online = devices.filter(isOnline).length;
        const offline = total - online;
        const gps = devices.filter(
            (d) =>
                typeof d.latitude === "number" &&
                typeof d.longitude === "number"
        ).length;

        return { total, online, offline, gps };
    }, [devices]);

    return (
        <div className="w-full">
            <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900">
                        IoT Live Control Center
                    </h1>
                    <p className="text-slate-600 mt-2">
                        Real-time IoT fleet monitoring, telemetry diagnostics,
                        GPS status and live device events.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={loadDevices}
                    className="rounded-xl bg-slate-800 px-5 py-3 text-white font-semibold hover:bg-slate-900 transition"
                >
                    Refresh Devices
                </button>
            </div>

            {error && (
                <div className="mb-4 rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
                <StatCard title="Total Devices" value={stats.total} color="blue" />
                <StatCard title="Online Devices" value={stats.online} color="green" />
                <StatCard title="Offline Devices" value={stats.offline} color="red" />
                <StatCard title="GPS Active" value={stats.gps} color="purple" />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2 bg-white rounded-2xl shadow border border-slate-200 overflow-hidden">
                    <div className="p-6 border-b">
                        <h2 className="text-2xl font-bold">Device Fleet</h2>
                        <p className="text-slate-500 mt-1">
                            Live telemetry and connectivity state of all IoT devices.
                        </p>
                    </div>

                    <div className="p-6">
                        {loading ? (
                            <p className="text-slate-500">Loading IoT devices...</p>
                        ) : devices.length === 0 ? (
                            <p className="text-slate-500">No IoT devices found.</p>
                        ) : (
                            <div className="grid gap-4">
                                {devices.map((d) => (
                                    <DeviceCard key={d.id || d.trackingNumber} device={d} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow border border-slate-200 overflow-hidden">
                    <div className="p-6 border-b">
                        <h2 className="text-2xl font-bold">Live IoT Events</h2>
                        <p className="text-slate-500 mt-1">
                            Latest real-time messages from the IoT stream.
                        </p>
                    </div>

                    <div className="p-6">
                        {liveEvents.length === 0 ? (
                            <p className="text-slate-500">
                                Waiting for live IoT events...
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {liveEvents.map((e) => (
                                    <div
                                        key={e.id}
                                        className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                    >
                                        <div className="font-bold text-slate-900">
                                            {e.type}
                                        </div>
                                        <div className="text-slate-600 mt-1">
                                            {e.message}
                                        </div>
                                        <div className="text-xs text-slate-400 mt-2">
                                            {e.time}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function isOnline(device) {
    if (!device.lastUpdated) return false;
    return Date.now() - new Date(device.lastUpdated).getTime() < 2 * 60 * 1000;
}

function format(value, suffix) {
    if (value === null || value === undefined || value === "") return "�";
    const number = Number(value);
    if (Number.isNaN(number)) return "�";
    return `${number.toFixed(1)} ${suffix}`;
}

function StatCard({ title, value, color = "blue" }) {
    const colors = {
        blue: "bg-blue-50 text-blue-700",
        green: "bg-green-50 text-green-700",
        red: "bg-red-50 text-red-700",
        purple: "bg-purple-50 text-purple-700",
    };

    return (
        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 hover:shadow-xl transition">
            <p className="text-slate-500">{title}</p>
            <div
                className={`inline-flex mt-3 px-3 py-1 rounded-xl font-black text-3xl ${colors[color]}`}
            >
                {value}
            </div>
        </div>
    );
}

function DeviceCard({ device }) {
    const online = isOnline(device);

    return (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 hover:bg-white hover:shadow transition">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <div>
                    <h3 className="text-lg font-black text-slate-900">
                        {device.name ||
                            device.deviceName ||
                            device.trackingNumber ||
                            "IoT Device"}
                    </h3>
                    <p className="text-slate-500 text-sm">
                        Tracking: {device.trackingNumber || "�"}
                    </p>
                </div>

                <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${online
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                >
                    {online ? "? Online" : "? Offline"}
                </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 text-sm">
                <Info label="Latitude" value={device.latitude ?? "�"} />
                <Info label="Longitude" value={device.longitude ?? "�"} />
                <Info label="Speed" value={format(device.speedKmh, "km/h")} />
                <Info label="Heading" value={format(device.headingDegrees, "�")} />
                <Info label="Accuracy" value={format(device.accuracyMeters, "m")} />
                <Info label="Battery" value={format(device.batteryLevel, "%")} />
                <Info label="Signal" value={format(device.signalQuality, "%")} />
                <Info label="Temperature" value={format(device.temperature, "�C")} />
                <Info label="Humidity" value={format(device.humidity, "%")} />
                <Info
                    label="Last Updated"
                    value={
                        device.lastUpdated
                            ? new Date(device.lastUpdated).toLocaleString()
                            : "�"
                    }
                />
            </div>
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div className="bg-white rounded-xl border border-slate-200 p-3">
            <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                {label}
            </p>
            <p className="font-bold text-slate-900 mt-1 break-words">
                {value}
            </p>
        </div>
    );
}